<script setup lang="ts">
const { t } = useLocale();

export interface ProxyTypeModel {
  title: string,
  desc: string,
  img: string,
  reversed?: boolean,
  type?: string,
  titleMaxWidth?: string,
  descMaxWidth?: string,
  height?: string;
  paddingLeft?: string;
  paddingRight?: string;
  gap?: string;
  hero?: boolean;
  heroTitle?: {
    first: string,
    second: string,
    third: string,
    color: string,
  };
  imgMaxWidth?: string,
}

const props = defineProps<ProxyTypeModel>();
</script>

<template>
  <div
    class="proxy-type-section"
    :class="{ 'proxy-type-section--reversed': reversed, 'proxy-type-section--hero': hero }"
    :style="{ height: `${height}px`, 'padding-left': `${paddingLeft}px`, 'padding-right': `${paddingRight}px`, gap: `${gap}px`  }"
  >
    <div
      class="proxy-type-section__item"
      :class="{ 'proxy-type-section__item--end': reversed }"
    >
      <div class="proxy-type-section__info">
        <h2
          class="sx-landing-title proxy-type-section__title"
          :style="{ 'max-width': `${titleMaxWidth}px` }"
        >
          <template v-if="title">{{ t(title) }}</template>
          <template v-else-if="!title && heroTitle">
            <span style="display: block">{{ t(heroTitle.first) }}</span>
            <span style="display: inline-block;margin-right: 12px">{{ t(heroTitle.second) }}</span>
            <span :style="{ color: heroTitle.color }"> {{ t(heroTitle.third) }}</span>
          </template>
        </h2>
        <p
          class="sx-landing-subtitle proxy-type-section__desc"
          :style="{ 'max-width': `${descMaxWidth}px` }"
        >
          {{ t(desc) }}
        </p>
        <div class="proxy-type-section__slot-wrapper">
          <slot />
        </div>
      </div>
    </div>

    <div
      class="proxy-type-section__item proxy-type-section__item--centered"
      :class="{ 'proxy-type-section__item--reversed': reversed }"
    >
      <div :style="{ 'max-width': `${imgMaxWidth}px` }">
        <img :src="`/sx/${props.img}`" alt="proxy-type" loading="lazy">
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.proxy-type-section {
  font-family: AlsHauss, sans-serif;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &--hero {
    justify-content: center;
    padding: 0 .5rem !important;
    gap: 16px;

    img {
      width: 100%;
      animation: none !important;
    }
  }

  @media (width < 1080px) {
    flex-direction: column-reverse !important;
    padding: 64px 2rem 2rem !important;
    height: auto !important;
    gap: 32px;
    justify-content: center;

    &__item div {
      display: flex;
      justify-content: center;
    }
    &__item img {
      width: 60%;
    }

    .sx-landing-title {
      text-align: center;
    }

    &__info {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    &__title {
      max-width: 100vw !important;
    }
    &__desc {
      max-width: 690px!important;
    }
  }

  @media (width < 600px) {
    padding: 2rem !important;
  }

  &.min &__title {
    width: min-content;
  }

  &--reversed {
    flex-direction: row-reverse;
  }

  &__content {
    max-width: 1440px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    align-items: center;

    &--reversed {
      flex-direction: row-reverse;
    }
  }

  &__item {

    &--end {
      display: flex;
      justify-content: flex-end;
    }

    &--centered {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &--reversed {
      justify-content: flex-start;
    }

    @media (width < 1080px) {
      justify-content: center;
    }
  }

  &__desc {
    margin-top: 32px;
  }
}

@keyframes floatAnimation {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.proxy-type-section__item img {
  animation: floatAnimation 3s ease-in-out infinite;
}

@media (max-width: 999px) {
  .proxy-type-section {
    &__content {
      flex-direction: column;
      gap: 24px;
    }

    &__item {
      width: 100%;

      &--reversed {
        justify-content: center;
      }
    }

    &__slot-wrapper {
      display: flex;
      justify-content: center;
    }
  }
}

@media (max-width: 699px) {
  .proxy-type-section {
    &__desc {
      width: auto !important;
      font-size: 18px;
      line-height: 22px;
    }
  }
}
</style>
